import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { GrowthOverviewGranularity, GrowthOverviewMetric } from "../const";
import {
    DEFAULT_GROWTH_OVERVIEW_SORT,
    type GrowthOverviewLevel,
    type IGrowthOverviewSort,
    type IGrowthOverviewTableRow,
} from "../types";
import { findGrowthOverviewRowById } from "../utils/findGrowthOverviewRowById";
import { getAutoExpandedRowKeys } from "../utils/getAutoExpandedRowKeys";
import { buildGrowthHeatmapGetStyle } from "../utils/heatmapScale";
import { isGrowthOverviewRowExpandable } from "../utils/isGrowthOverviewRowExpandable";
import { getPeriodsForMetric } from "../utils/mapApiRows";
import { normalizePop } from "../utils/normalizePop";
import { getNextGrowthOverviewSort } from "../utils/sortTableRows";
import {
    getExpandedRowKeysAfterToggle,
    getShowAllDomainsRowKeysAfterToggle,
} from "../utils/toggleGrowthOverviewExpansion";

const collectHeatmapPopValues = (rows: IGrowthOverviewTableRow[], metric: GrowthOverviewMetric): (number | null)[] =>
    rows.flatMap((row) => {
        const rowPops = getPeriodsForMetric(row, metric).map((period) => normalizePop(period.pop));
        const childPops = row.children?.length ? collectHeatmapPopValues(row.children, metric) : [];

        return [...rowPops, ...childPops];
    });

export const useGrowthOverviewTableSort = (granularity: GrowthOverviewGranularity, metric: GrowthOverviewMetric) => {
    const [sort, setSort] = useState<IGrowthOverviewSort>(DEFAULT_GROWTH_OVERVIEW_SORT);

    useEffect(() => {
        setSort(DEFAULT_GROWTH_OVERVIEW_SORT);
    }, [granularity, metric]);

    const handleSort = useCallback((field: string) => {
        setSort((current) => getNextGrowthOverviewSort(current, field));
    }, []);

    return { sort, handleSort };
};

export const useGrowthOverviewRowExpansion = (
    rows: IGrowthOverviewTableRow[],
    rawRows: IGrowthOverviewTableRow[],
    responseLevel: GrowthOverviewLevel | undefined,
    category: string,
    loadBranch: (
        row: IGrowthOverviewTableRow,
        options?: { domainOffset?: number; subIndustryOffset?: number },
    ) => Promise<void>,
    loadingRowIds: ReadonlySet<string>,
) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [showAllDomainsRowKeys, setShowAllDomainsRowKeys] = useState<ReadonlySet<string>>(() => new Set());
    const previousCategoryRef = useRef(category);

    useEffect(() => {
        setShowAllDomainsRowKeys(new Set());
    }, [rawRows]);

    useLayoutEffect(() => {
        const categoryChanged = previousCategoryRef.current !== category;
        previousCategoryRef.current = category;

        const autoExpandedRowKeys = getAutoExpandedRowKeys(rawRows, responseLevel);
        if (autoExpandedRowKeys) {
            setExpandedRowKeys(autoExpandedRowKeys);
        } else if (categoryChanged) {
            setExpandedRowKeys(new Set());
        }

        if (!autoExpandedRowKeys) {
            return;
        }

        autoExpandedRowKeys.forEach((rowId) => {
            const row = findGrowthOverviewRowById(rawRows, rowId);
            if (row && isGrowthOverviewRowExpandable(row) && !row.childrenLoaded && !loadingRowIds.has(rowId)) {
                void loadBranch(row);
            }
        });
    }, [category, rawRows, responseLevel, loadBranch, loadingRowIds]);

    const handleToggleRow = useCallback(
        (row: IGrowthOverviewTableRow) => {
            if (row.rowType === "domain" || !isGrowthOverviewRowExpandable(row)) {
                return;
            }

            const isCollapsing = expandedRowKeys.has(row.id);

            setExpandedRowKeys((current) =>
                getExpandedRowKeysAfterToggle({
                    row,
                    rows,
                    expandedRowKeys: current,
                }),
            );

            setShowAllDomainsRowKeys((current) => getShowAllDomainsRowKeysAfterToggle(row, expandedRowKeys, current));

            if (!isCollapsing && !row.childrenLoaded) {
                void loadBranch(row);
            }
        },
        [expandedRowKeys, rows, loadBranch],
    );

    const handleShowAllDomains = useCallback(
        (rowId: string) => {
            const row = findGrowthOverviewRowById(rows, rowId);
            if (!row) {
                return;
            }

            const loadedCount = row.children?.length ?? 0;
            const totalCount = row.totalDomainCount ?? row.domainCount ?? loadedCount;
            const isShowingAllLoaded = showAllDomainsRowKeys.has(rowId);

            if (!isShowingAllLoaded) {
                setShowAllDomainsRowKeys((current) => new Set(current).add(rowId));
                return;
            }

            if (loadedCount < totalCount) {
                void loadBranch(row, { domainOffset: loadedCount });
            }
        },
        [rows, showAllDomainsRowKeys, loadBranch],
    );

    const handleShowMoreSubIndustries = useCallback(
        (rowId: string): number | undefined => {
            const row = findGrowthOverviewRowById(rows, rowId);
            if (!row) {
                return undefined;
            }

            const loadedCount = row.children?.length ?? 0;
            void loadBranch(row, { subIndustryOffset: loadedCount });
            return loadedCount;
        },
        [rows, loadBranch],
    );

    return {
        expandedRowKeys,
        showAllDomainsRowKeys,
        loadingRowIds,
        handleToggleRow,
        handleShowAllDomains,
        handleShowMoreSubIndustries,
    };
};

export const useGrowthOverviewHeatmap = (rows: IGrowthOverviewTableRow[], metric: GrowthOverviewMetric) => {
    const heatmapPopValues = useMemo(() => collectHeatmapPopValues(rows, metric), [rows, metric]);

    const getHeatmapStyle = useMemo(() => buildGrowthHeatmapGetStyle(heatmapPopValues), [heatmapPopValues]);

    const getHeatmapColor = useMemo(
        () => (pop: number | null | undefined) => getHeatmapStyle(pop).backgroundColor,
        [getHeatmapStyle],
    );

    return { getHeatmapStyle, getHeatmapColor };
};
